import {
  CreateButton,
  DateField,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  UrlField,
  useSelect,
  useTable,
} from "@refinedev/antd";

// It is recommended to use explicit import as seen below to reduce bundle size.
// import { IconName } from "@ant-design/icons";
import * as Icons from "@ant-design/icons";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";

import { IPhysicalBooks, IVessel } from "interfaces";
import React from "react";

export const PhysicalBooksList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IPhysicalBooks>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } =
    useExport<IPhysicalBooks>({
      mapData: (item) => {
        return {
          "Shipco": item?.licence?.shipco?.registrationName || "",
          "Vessel": item?.licence?.vessel?.name || "",
          "File Name": item?.fileName || "",
          "Content Type": item?.contentType || "",
        };
      },
      unparseConfig: {
        header: true,
        columns: ["Shipco", "Vessel", "File Name", "Content Type"],
      },
    });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IPhysicalBooks> = [
    {
      title: "Shipco",
      dataIndex: ["licence", "shipco", "registrationName"],
      key: "licence.shipco.registrationName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
    },
    {
      title: "Vessel",
      dataIndex: ["licence", "vessel", "name"],
      key: "licence.vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 250 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "File Name",
      dataIndex: "fileName",
      key: "fileName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("fileName", sorters),
      sorter: true,
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      className: "wrap-text",
      render: (value) => (
        <DateField format="DD-MMM-YYYY hh:mm:ss A" value={value} />
      ),
      defaultSortOrder: getDefaultSortOrder("createdAt", sorters),
      sorter: true,
    },
    {
      title: "Download",
      dataIndex: "storageUrl",
      key: "storageUrl",
      width: 100,
      render: (value) => (
        <UrlField value={value}>
          <Icons.DownloadOutlined />
        </UrlField>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton
            disabled
            hideText
            size="small"
            recordItemId={record.id}
          />
          <ShowButton
            disabled
            hideText
            size="small"
            recordItemId={record.id}
          />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "Report",
        subTitle: "standard library publications",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
